import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { functions } from '@/lib/firebase/config';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  REMIND_MAX_IDS,
  confirmReminderText,
  reminderSummary,
  type RemindQuestionnaireResult,
} from '@/lib/assessment/questionnaireReminder';

interface RemindQuestionnaireButtonProps {
  organizationId: string;
  /** The beneficiaries in the current filtered list whose questionnaire is not valid. */
  userIds: string[];
}

/**
 * «تذكيرهم» (#97): after a confirm, asks the server to remind the listed
 * beneficiaries to fill the questionnaire. The server re-checks each one and
 * applies the weekly cap; the toast reports its counts.
 */
export function RemindQuestionnaireButton({ organizationId, userIds }: RemindQuestionnaireButtonProps) {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const call = httpsCallable<{ organizationId: string; userIds: string[] }, RemindQuestionnaireResult>(
        functions,
        'remindQuestionnaire',
      );
      let total: RemindQuestionnaireResult | null = null;
      for (let i = 0; i < userIds.length; i += REMIND_MAX_IDS) {
        const { data } = await call({ organizationId, userIds: userIds.slice(i, i + REMIND_MAX_IDS) });
        total = total
          ? {
              mode: data.mode,
              sent: total.sent + data.sent,
              deferred: total.deferred + data.deferred,
              wouldSend: total.wouldSend + data.wouldSend,
              skippedCap: total.skippedCap + data.skippedCap,
              skippedValid: total.skippedValid + data.skippedValid,
              skippedNotEligible: total.skippedNotEligible + data.skippedNotEligible,
              failed: total.failed + data.failed,
            }
          : data;
      }
      if (total) {
        const message = reminderSummary(total, isArabic);
        if (total.mode === 'dry_run') toast.info(message);
        else toast.success(message);
      }
    } catch (error) {
      console.error('remindQuestionnaire failed', error);
      toast.error(isArabic ? 'تعذّر إرسال التذكير' : 'Could not send the reminder');
    } finally {
      setSending(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={sending || userIds.length === 0 || !organizationId}
        onClick={() => setOpen(true)}
        data-testid="remind-questionnaire"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
        {isArabic ? 'تذكيرهم' : 'Remind them'}
      </Button>
      <AlertDialog open={open} onOpenChange={(next) => !sending && setOpen(next)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmReminderText(userIds.length, isArabic)}</AlertDialogTitle>
            <AlertDialogDescription>
              {isArabic
                ? 'سيصلهم إشعار بملء استبيان مقياس نمط. من ذُكّر خلال آخر 7 أيام لن يُرسل له.'
                : 'They will get a notification to fill the Namat questionnaire. Anyone reminded in the last 7 days is skipped.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>{isArabic ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              disabled={sending}
              onClick={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              {isArabic ? 'إرسال' : 'Send'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
